<script setup lang="ts">
import { computed } from 'vue';

import type { CartsTransactionsApi } from '@/services/booking/cartsTransactionsApi';
import type { CartDetailsItemResponse } from '@/types/carts-transactions-api';

import CartItemError from './CartItemError.vue';
import CartItemExpired from './CartItemExpired.vue';
import CartItemLoaded from './CartItemLoaded.vue';
import CartItemSkeleton from './CartItemSkeleton.vue';

const props = defineProps<{
  item: CartDetailsItemResponse;
  enrichedUnits: Array<{
    id: string;
    description: string;
    quantity: number;
    originalPricePerUnit: number;
    netPricePerUnit: number;
  }>;
  currency?: string;
  currencyPrecision?: number;
  dateTime: string;
  isDeleting: boolean;
  isExpired: boolean;
  isPartialFailure: boolean;
  isItemFailed: boolean;
  hasPendingTransaction: boolean;
  cartsTransactionsApi: CartsTransactionsApi;
}>();

const emit = defineEmits<{
  'delete': [];
  'expired': [];
  'update:contactFieldsCompleted': [isCompleted: boolean];
}>();

const personCount = computed(() =>
  props.item.units.reduce((sum, unit) => sum + unit.quantity, 0),
);
</script>

<template>
  <div class="container" :class="{ 'item-failed': isPartialFailure && isItemFailed }">
    <CartItemError
      v-if="item.bookingError"
      :disabled="hasPendingTransaction"
      @delete="emit('delete')"
    />

    <CartItemExpired
      v-else-if="isExpired"
      :product-id="item.productId"
      :image-url="item.bookingDetails?.productImageUrl ?? null"
      :product-name="item.bookingDetails?.productName ?? ''"
      :date-time="dateTime"
      :person-count="personCount"
      :is-deleting="isDeleting"
      :disabled="hasPendingTransaction"
      @delete="emit('delete')"
    />

    <CartItemSkeleton v-else-if="!item.bookingDetails" />

    <CartItemLoaded
      v-else
      :item="item"
      :enriched-units="enrichedUnits"
      :currency="currency"
      :currency-precision="currencyPrecision"
      :date-time="dateTime"
      :person-count="personCount"
      :is-deleting="isDeleting"
      :is-partial-failure="isPartialFailure"
      :has-pending-transaction="hasPendingTransaction"
      :carts-transactions-api="cartsTransactionsApi"
      @delete="emit('delete')"
      @expired="emit('expired')"
      @update:contact-fields-completed="(value: boolean) => emit('update:contactFieldsCompleted', value)"
    />
  </div>
</template>

<style scoped lang="css">
.container {
  display: flex;
  flex-direction: column;
  gap: var(--yb-spacing-sm);
  border: var(--yb-color-mediumgray) solid 1px;
  padding: var(--yb-spacing-sm);
  border-radius: var(--yb-radius-base);
}

.item-failed {
  border-color: #dc2626;
  border-width: 2px;
}
</style>
